import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  KeyStatusBadge, PageHeader, Pager, ProgressBar, RepoStatusBadge, RiskBadge, SampleLink, SecretsTabs, errMsg, fmtDate,
  useSecretFilters, useSecretsAccess, useUrlFilters,
} from './common';
import { BulkBar, KeyFilters } from './SecretKeysList';

// Keys that were in a repo's current code at the 23 Sep baseline scan and are
// no longer there. Uses the Keys page filter bar; sign-off is per key-in-repo.

const FILTER_KEYS = ['q', 'status', 'risk', 'type', 'reachability', 'team', 'owner', 'repo_id', 'repo_status', 'gone_reason',
  'revoker', 'jira', 'present', 'sort', 'dir', 'page', 'limit'];
const GONE_SORTS = [['removed', 'Detected gone'], ['risk', 'Risk'], ['repo', 'Repo'], ['owner', 'Owner'], ['status', 'Sign-off']];

function Stat({ label, value, sub }) {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

// One row per key per scope (repo root or owned folder); selection by row id.
const pairKey = (r) => String(r.key_repo_id);

export default function SecretGone() {
  const { params, get, set } = useUrlFilters();
  const filters = useSecretFilters();
  const { can_edit: canEdit } = useSecretsAccess();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [confirmedBy, setConfirmedBy] = useState('');
  const [msg, setMsg] = useState('');
  const query = params.toString();

  const filterParams = useCallback(() => {
    const p = {};
    FILTER_KEYS.forEach((k) => { if (get(k)) p[k] = get(k); });
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const load = useCallback(() => {
    setLoading(true); setError('');
    axios.get('/secrets/gone', { params: filterParams() })
      .then((r) => setD(r.data)).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
  }, [filterParams]);
  useEffect(() => { load(); setSelected(new Set()); }, [load]);

  const signoff = (status, allMatching) => {
    const n = allMatching ? d.open : selected.size;
    const what = status === 'open' ? `Re-open ${selected.size} selected pair(s)?`
      : `Mark ${n} key-in-repo pair(s) ready to revoke on the owners' behalf?`;
    if (!window.confirm(what)) return;
    const body = { status, confirmed_by: confirmedBy };
    if (allMatching) body.all_matching = true;
    else body.rows = [...selected].map(Number);
    const p = { ...filterParams() };
    delete p.page; delete p.limit; delete p.sort; delete p.dir;
    setMsg('');
    axios.post('/secrets/gone/signoff', body, { params: allMatching ? p : undefined })
      .then((r) => { setMsg(`${r.data.changed} pair(s) updated`); load(); })
      .catch((e) => setMsg(errMsg(e)));
  };

  const rows = d?.data || [];
  const allOn = rows.length > 0 && rows.every((r) => selected.has(pairKey(r)));
  const t = d?.totals || {};

  return (
    <div>
      <PageHeader title="Secrets rotation · Gone from code">
        Keys that were in a repo's current code at the 23 Sep scan and the presence scan no longer finds there.
        They still exist in git history, so each one still needs revoking. Mark them ready here when an owner forgets.
        Keys marked false positive count as gone wherever they are, and need no sign-off.
        Keys in repos archived on Bitbucket count as gone too: those repos are no longer scanned.
      </PageHeader>
      <div className="page-body">
        <SecretsTabs />
        {d && (
          <div className="card-grid">
            <Stat label="In code at 23 Sep" value={(t.baseline_pairs || 0).toLocaleString()}
              sub={`key-in-repo pairs, ${(t.baseline_keys || 0).toLocaleString()} keys`} />
            <Stat label="Gone from code" value={(t.gone_pairs || 0).toLocaleString()}
              sub={`${Math.round((100 * (t.gone_pairs || 0)) / (t.baseline_pairs || 1))}% of those pairs`} />
            {(t.gone_archived || 0) > 0 && (
              <Link to="/secrets/gone?gone_reason=archived" style={{ textDecoration: 'none' }}>
                <Stat label="Gone by archiving the repo" value={(t.gone_archived || 0).toLocaleString()}
                  sub={`pairs in ${t.gone_archived_repos || 0} repos archived on Bitbucket, false positives aside; no longer scanned`} />
              </Link>
            )}
            <Stat label="Gone and signed off" value={(t.gone_ready || 0).toLocaleString()}
              sub={`${(t.gone_waiting || 0).toLocaleString()} still waiting for sign-off, false positives aside`} />
            <Stat label="Gone, false positive" value={(t.gone_fp || 0).toLocaleString()}
              sub={`pairs, ${(t.gone_fp_keys || 0).toLocaleString()} keys (${(t.gone_fp_auto_keys || 0).toLocaleString()} auto-marked)`} />
            <Stat label="Keys out of code everywhere" value={(t.keys_out_everywhere || 0).toLocaleString()}
              sub="gone from every repo that held them, or false positive" />
            {t.partial && (
              <Link to="/secrets/keys?progress=partial" style={{ textDecoration: 'none' }}>
                <Stat label="Partly cleaned, not gone yet" value={(t.partial.keys_ || 0).toLocaleString()}
                  sub={`keys in ${t.partial.repos || 0} repos with ${(t.partial.files_cleaned || 0).toLocaleString()} of ${((t.partial.files_cleaned || 0) + (t.partial.files_left || 0)).toLocaleString()} files cleaned; they count as gone once every file is clean`} />
              </Link>
            )}
          </div>
        )}

        {d && d.by_team.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>By team</h3>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Team</th><th>In code at 23 Sep</th><th>Gone</th><th>Gone share</th><th>Gone and signed off</th><th>Gone, false positive</th></tr></thead>
                <tbody>
                  {d.by_team.map((r) => (
                    <tr key={r.team} style={{ cursor: 'pointer' }} onClick={() => set({ team: r.team })}>
                      <td>{r.team}</td>
                      <td>{r.baseline_pairs}</td>
                      <td>{r.gone_pairs}</td>
                      <td><ProgressBar value={r.gone_pairs} total={r.baseline_pairs} color="var(--success)" /></td>
                      <td>{r.gone_ready} of {r.gone_pairs}</td>
                      <td>{r.gone_fp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <KeyFilters get={get} set={set} filters={filters} showRepoStatus showProgress={false} showGoneReason sorts={GONE_SORTS}
          defaultSort="removed" defaultDir="desc" />

        {canEdit && d && (
          <BulkBar keyIds={[...new Set([...selected].map((k) => Number(k.split(':')[0])))]} total={d.keys_total}
            scope="gone" filterParams={filterParams()}
            onDone={() => { setSelected(new Set()); load(); }} onClear={() => setSelected(new Set())}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10,
              paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Repo sign-off for these pairs:</span>
              <input className="form-input" style={{ width: 200 }} placeholder="Confirmed by (optional)"
                value={confirmedBy} onChange={(e) => setConfirmedBy(e.target.value)} />
              <button className="btn btn-secondary" disabled={!selected.size} onClick={() => signoff('ready_to_revoke', false)}>
                Mark {selected.size || ''} selected ready
              </button>
              <button className="btn btn-secondary" disabled={!d.open} onClick={() => signoff('ready_to_revoke', true)}>
                Mark all {d.open} open matches ready
              </button>
              <button className="btn btn-ghost" disabled={!selected.size} onClick={() => signoff('open', false)}>Re-open selected</button>
              {msg && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
            </div>
          </BulkBar>
        )}

        {error && <div className="error-msg">{error}</div>}
        <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
          {d ? `${d.total} pairs match, ${d.open} not signed off, ${d.false_positive} false positive` : ''} {loading && <span className="spinner" />}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  {canEdit && <input type="checkbox" checked={allOn} onChange={() => {
                    const n = new Set(selected);
                    rows.forEach((r) => (allOn ? n.delete(pairKey(r)) : n.add(pairKey(r))));
                    setSelected(n);
                  }} />}
                </th>
                <th>Key</th><th>Risk</th><th>Type · use (guess)</th><th>Repo / folder</th><th>Detected gone</th>
                <th>Elsewhere</th><th>Sign-off</th><th>Key status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={pairKey(r)}>
                  <td>{canEdit && <input type="checkbox" checked={selected.has(pairKey(r))} onChange={() => {
                    const n = new Set(selected);
                    if (n.has(pairKey(r))) n.delete(pairKey(r)); else n.add(pairKey(r));
                    setSelected(n);
                  }} />}</td>
                  <td><Link to={`/secrets/keys/${r.key_id}`}><code style={{ fontSize: 12 }}>{r.key_redacted}</code></Link>
                    <SampleLink r={r} /></td>
                  <td><RiskBadge risk={r.risk_level} type={r.secret_type} /></td>
                  <td style={{ maxWidth: 320, fontSize: 12 }}>
                    <div style={{ color: 'var(--text-secondary)' }}>{r.secret_type}</div>{r.key_use}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <Link to={`/secrets/repos/${r.repo_id}`}>{r.repo_slug}</Link>
                    {r.folder_path && <div style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>folder {r.folder_path}</div>}
                    <div style={{ color: 'var(--text-muted)' }}>{r.owner_name || '—'} · {r.team || 'Unknown'}</div>
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {r.removed_detected_at ? fmtDate(r.removed_detected_at)
                      : r.key_status === 'false_positive' ? <span style={{ color: 'var(--text-muted)' }}>Still in code, false positive</span>
                        : r.key_status === 'wont_fix' ? <span style={{ color: 'var(--text-muted)' }}>Still in code, won't fix</span> : '—'}
                    {r.removed_reason === 'repo_archived' && (
                      <div style={{ color: 'var(--text-muted)' }} title="Repo archived on Bitbucket; no longer scanned">Repo archived</div>
                    )}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.present_in_any_repo
                      ? <span style={{ color: '#fbbf24' }}>Still in code in another repo</span>
                      : <span style={{ color: 'var(--text-muted)' }}>Out of code everywhere</span>}
                    {r.repo_count > 1 && <div style={{ color: 'var(--text-muted)' }}>{r.repo_count} repos</div>}
                  </td>
                  <td>
                    <RepoStatusBadge status={r.repo_status} />
                    {r.repo_status === 'ready_to_revoke' && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        {r.confirmed_by ? `by ${r.confirmed_by}, ` : ''}{fmtDate(r.marked_at)}
                      </div>
                    )}
                  </td>
                  <td><KeyStatusBadge status={r.key_status} /></td>
                </tr>
              ))}
              {d && rows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
                  No pairs match these filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {d && <Pager page={d.page} limit={d.limit} total={d.total} onPage={(p) => set({ page: p }, { keepPage: true })} />}
      </div>
    </div>
  );
}
